import { useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom-v5-compat'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { api, queryKeys } from '~/api'
import { formatReturnDate, formatReturnMoney } from '~/returns/components'
import type {
    ReturnActionCapability,
    ReturnCapabilities,
    ReturnDetail,
    ReturnMedia,
} from '~/returns/schema'

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <Paper sx={{ mb: 2, px: 2.5, py: 2, color: 'grey.700' }}>
            <Typography
                gutterBottom
                variant="subtitle2"
                sx={{ fontSize: '0.9rem', fontWeight: 600 }}
            >
                {title}
            </Typography>
            {children}
        </Paper>
    )
}

function DetailValue({ label, value }: { label: string; value?: React.ReactNode }) {
    return (
        <Box>
            <Typography variant="caption" color="grey.500">
                {label}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.25, overflowWrap: 'anywhere' }}>
                {value || '—'}
            </Typography>
        </Box>
    )
}

function MediaGrid({ media }: { media: ReturnMedia[] }) {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                gap: 1,
            }}
        >
            {media.map((item) => (
                <Link key={item.url} href={item.url} target="_blank" rel="noreferrer">
                    {item.type === 'video' ? (
                        <Box
                            component="video"
                            src={item.url}
                            poster={item.thumbnail_url || undefined}
                            controls
                            sx={{
                                width: '100%',
                                height: 110,
                                objectFit: 'cover',
                                borderRadius: 1,
                            }}
                        />
                    ) : (
                        <Box
                            component="img"
                            src={item.url}
                            alt=""
                            sx={{
                                width: '100%',
                                height: 110,
                                objectFit: 'cover',
                                borderRadius: 1,
                            }}
                        />
                    )}
                </Link>
            ))}
        </Box>
    )
}

function actionColor(action: string): 'secondary' | 'error' | 'inherit' {
    if (action.startsWith('reject') || action === 'dispute') return 'error'
    if (
        action.startsWith('approve') ||
        action === 'confirm_received' ||
        action === 'refund_without_return'
    )
        return 'secondary'
    return 'inherit'
}

function actionVariant(action: string): 'contained' | 'outlined' | 'text' {
    if (action === 'upload_evidence') return 'text'
    return actionColor(action) === 'secondary' ? 'contained' : 'outlined'
}

function getApiError(body: unknown, fallback: string) {
    if (body && typeof body === 'object' && 'error' in body) {
        const error = (body as { error?: unknown }).error
        if (typeof error === 'string') return error
    }
    return fallback
}

function getRequestError(error: unknown, fallback: string) {
    if (error instanceof Error) return error.message
    if (error && typeof error === 'object' && 'body' in error) {
        return getApiError((error as { body: unknown }).body, fallback)
    }
    return fallback
}

function ActionDialog({
    capability,
    onClose,
    onComplete,
    returnId,
}: {
    capability: ReturnActionCapability | null
    onClose: () => void
    onComplete: () => Promise<void>
    returnId: number
}) {
    const { enqueueSnackbar } = useSnackbar()
    const [reasonToken, setReasonToken] = useState('')
    const [comment, setComment] = useState('')
    const [images, setImages] = useState<File[]>([])
    const [moduleImages, setModuleImages] = useState<Record<number, File[]>>({})
    const [carrierId, setCarrierId] = useState('')
    const [carrierName, setCarrierName] = useState('')
    const [trackingNumber, setTrackingNumber] = useState('')
    const submitAction = api.returns.submitAction.useMutation()

    const capabilityQuery = api.returns.capabilities.useQuery(
        queryKeys.returns.capabilities(returnId, capability?.action).queryKey,
        { params: { returnId }, query: { action: capability?.action } },
        { enabled: Boolean(capability), staleTime: 15 * 1000 }
    )
    const body = capabilityQuery.data?.status === 200 ? capabilityQuery.data.body : undefined
    const carriers = body?.context.shipping_carriers || []
    const shippingProofTemplate = body?.context.shipping_proof_template || []
    const trackingNumberRequired = shippingProofTemplate.some(
        (template) => template.is_tracking_number_required
    )
    const shippingImageRequired = shippingProofTemplate.some(
        (template) => template.is_shipping_image_file_mandatory
    )
    const selectedReason = body?.reasons.find((reason) => reason.token === reasonToken)
    const maxImages = capability?.max_images ?? 0
    const usesEvidenceModules = selectedReason?.evidence_mode === 'module'
    const missingRequiredEvidence = Boolean(
        selectedReason?.evidence_requirements.some(
            (requirement) =>
                requirement.is_required && !moduleImages[requirement.module_index]?.length
        )
    )
    const moduleImageCount = Object.values(moduleImages).reduce(
        (total, files) => total + files.length,
        0
    )

    const submit = async () => {
        if (!capability) return
        if (capability.requires_reason && !reasonToken) {
            enqueueSnackbar(gettext('Select a reason before continuing.'), { variant: 'warning' })
            return
        }
        if (capability.comment_requirement === 'required' && !comment.trim()) {
            enqueueSnackbar(gettext('Enter a comment before continuing.'), {
                variant: 'warning',
            })
            return
        }
        if (missingRequiredEvidence) {
            enqueueSnackbar(gettext('Add evidence for every required item.'), {
                variant: 'warning',
            })
            return
        }
        if (
            (capability.evidence === 'required' || shippingImageRequired) &&
            images.length + moduleImageCount === 0
        ) {
            enqueueSnackbar(gettext('Add at least one evidence image.'), { variant: 'warning' })
            return
        }
        if (capability.action === 'upload_shipping_proof' && (!carrierId || !carrierName)) {
            enqueueSnackbar(gettext('Select a carrier.'), { variant: 'warning' })
            return
        }
        if (
            capability.action === 'upload_shipping_proof' &&
            trackingNumberRequired &&
            !trackingNumber
        ) {
            enqueueSnackbar(gettext('Enter the carrier tracking number.'), {
                variant: 'warning',
            })
            return
        }

        const form = new FormData()
        form.set('action', capability.action)
        form.set('idempotency_key', crypto.randomUUID())
        if (reasonToken) form.set('reason_token', reasonToken)
        if (comment) form.set('comment', comment)
        for (const image of images) form.append('images', image)
        for (const [moduleIndex, files] of Object.entries(moduleImages)) {
            for (const image of files) form.append(`evidence_${moduleIndex}`, image)
        }
        if (capability.action === 'upload_shipping_proof') {
            form.set('carrier_id', carrierId)
            form.set('carrier_name', carrierName)
            if (trackingNumber) form.set('tracking_number', trackingNumber)
        }

        try {
            await submitAction.mutateAsync({
                params: { returnId },
                body: form,
            })
            enqueueSnackbar(gettext('Return action submitted.'), { variant: 'success' })
            await onComplete()
            onClose()
        } catch (error) {
            enqueueSnackbar(getRequestError(error, gettext('Action failed.')), {
                variant: 'error',
            })
        }
    }

    const selectCarrier = (value: string) => {
        setCarrierId(value)
        const selected = carriers.find((carrier) => String(carrier.id) === value)
        setCarrierName(selected?.name || '')
    }

    return (
        <Dialog
            open={Boolean(capability)}
            onClose={submitAction.isLoading ? undefined : onClose}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>{capability?.label}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {capabilityQuery.isError ? (
                        <Typography color="error" variant="body2">
                            {getRequestError(
                                capabilityQuery.error,
                                gettext('Live action details could not be loaded.')
                            )}
                        </Typography>
                    ) : null}
                    {capability?.requires_reason ? (
                        <TextField
                            select
                            required
                            fullWidth
                            label={gettext('Decision reason')}
                            value={reasonToken}
                            onChange={(event) => {
                                setReasonToken(event.target.value)
                                setImages([])
                                setModuleImages({})
                            }}
                            disabled={capabilityQuery.isLoading}
                        >
                            {body?.reasons.map((reason) => (
                                <MenuItem key={reason.token} value={reason.token}>
                                    <Box>
                                        <Typography variant="body2">{reason.label}</Typography>
                                        {reason.channel_label !== reason.label ? (
                                            <Typography variant="caption" color="text.secondary">
                                                {gettext('Channel reason')}: {reason.channel_label}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                </MenuItem>
                            ))}
                        </TextField>
                    ) : null}
                    {selectedReason?.evidence_requirements.length ? (
                        <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                {gettext('Channel evidence requirements')}
                            </Typography>
                            {selectedReason.evidence_requirements.map((requirement) => {
                                const selected = moduleImages[requirement.module_index] || []
                                return (
                                    <Box key={requirement.module_index} sx={{ mt: 1.25 }}>
                                        <Typography variant="body2">
                                            {requirement.is_required ? '* ' : ''}
                                            {requirement.requirement}
                                        </Typography>
                                        <Button component="label" size="small" sx={{ mt: 0.5 }}>
                                            {selected.length
                                                ? `${selected.length} ${gettext('images selected')}`
                                                : gettext('Add images')}
                                            <input
                                                hidden
                                                multiple
                                                accept="image/*"
                                                type="file"
                                                onChange={(event) => {
                                                    const files = Array.from(
                                                        event.target.files || []
                                                    )
                                                    const otherImageCount =
                                                        moduleImageCount - selected.length
                                                    if (
                                                        otherImageCount + files.length >
                                                        maxImages
                                                    ) {
                                                        enqueueSnackbar(
                                                            interpolate(
                                                                gettext(
                                                                    'Select no more than %s images.'
                                                                ),
                                                                [maxImages]
                                                            ),
                                                            { variant: 'warning' }
                                                        )
                                                        event.target.value = ''
                                                        return
                                                    }
                                                    setModuleImages((current) => ({
                                                        ...current,
                                                        [requirement.module_index]: files,
                                                    }))
                                                }}
                                            />
                                        </Button>
                                    </Box>
                                )
                            })}
                        </Box>
                    ) : null}
                    {capability && capability.comment_requirement !== 'none' ? (
                        <TextField
                            required={capability.comment_requirement === 'required'}
                            multiline
                            minRows={3}
                            label={gettext('Comment')}
                            value={comment}
                            onChange={(event) => setComment(event.target.value)}
                        />
                    ) : null}
                    {capability?.action === 'upload_shipping_proof' ? (
                        <>
                            <TextField
                                select
                                required
                                label={gettext('Carrier')}
                                value={carrierId}
                                onChange={(event) => selectCarrier(event.target.value)}
                            >
                                {carriers.map((carrier) => (
                                    <MenuItem key={carrier.id} value={String(carrier.id)}>
                                        {carrier.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label={pgettext('Return logistics', 'Tracking number')}
                                required={trackingNumberRequired}
                                value={trackingNumber}
                                onChange={(event) => setTrackingNumber(event.target.value)}
                            />
                        </>
                    ) : null}
                    {capability && capability.evidence !== 'none' && !usesEvidenceModules ? (
                        <Button component="label" variant="outlined">
                            {images.length
                                ? `${images.length} ${gettext('images selected')}`
                                : gettext('Add evidence images')}
                            <input
                                hidden
                                multiple
                                accept="image/*"
                                type="file"
                                onChange={(event) => {
                                    const selected = Array.from(event.target.files || [])
                                    const limit = capability.max_images
                                    if (selected.length > limit) {
                                        enqueueSnackbar(
                                            interpolate(gettext('Select no more than %s images.'), [
                                                limit,
                                            ]),
                                            { variant: 'warning' }
                                        )
                                        event.target.value = ''
                                        return
                                    }
                                    setImages(selected)
                                }}
                            />
                        </Button>
                    ) : null}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    sx={{ color: 'common.black' }}
                    onClick={onClose}
                    disabled={submitAction.isLoading}
                >
                    {gettext('Cancel')}
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    disabled={submitAction.isLoading || capabilityQuery.isLoading || !body}
                    onClick={() => void submit()}
                >
                    {submitAction.isLoading ? (
                        <CircularProgress size={20} color="inherit" />
                    ) : (
                        capability?.label
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

function Notes({
    returnDetail,
    onChanged,
}: {
    returnDetail: ReturnDetail
    onChanged: () => Promise<void>
}) {
    const { enqueueSnackbar } = useSnackbar()
    const [text, setText] = useState('')
    const [images, setImages] = useState<File[]>([])
    const createNoteMutation = api.returns.createNote.useMutation()
    const deleteNoteMutation = api.returns.deleteNote.useMutation()

    const createNote = async () => {
        if (!text.trim() && !images.length) return
        const form = new FormData()
        form.set('text', text.trim())
        for (const image of images) form.append('images', image)
        try {
            await createNoteMutation.mutateAsync({
                params: { returnId: returnDetail.id },
                body: form,
            })
            setText('')
            setImages([])
            await onChanged()
        } catch (error) {
            enqueueSnackbar(getRequestError(error, gettext('Could not add note.')), {
                variant: 'error',
            })
        }
    }

    const deleteNote = async (noteId: number) => {
        try {
            await deleteNoteMutation.mutateAsync({
                params: { returnId: returnDetail.id, noteId },
            })
            await onChanged()
        } catch (error) {
            enqueueSnackbar(getRequestError(error, gettext('Could not delete note.')), {
                variant: 'error',
            })
        }
    }

    return (
        <Stack spacing={1.5}>
            <TextField
                multiline
                minRows={2}
                size="small"
                placeholder={gettext('Add a note')}
                value={text}
                onChange={(event) => setText(event.target.value)}
            />
            <Stack direction="row" spacing={1}>
                <Button component="label" size="small" variant="outlined" color="secondary">
                    {images.length ? `${images.length} ${gettext('files')}` : gettext('Attach')}
                    <input
                        hidden
                        multiple
                        accept="image/*"
                        type="file"
                        onChange={(event) => setImages(Array.from(event.target.files || []))}
                    />
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    disabled={createNoteMutation.isLoading || (!text.trim() && !images.length)}
                    onClick={() => void createNote()}
                >
                    {createNoteMutation.isLoading ? (
                        <CircularProgress size={18} color="inherit" />
                    ) : (
                        gettext('Add note')
                    )}
                </Button>
            </Stack>
            {returnDetail.notes.map((note) => (
                <Box key={note.id} sx={{ borderTop: 1, borderColor: 'divider', pt: 1.25 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {note.text}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {formatReturnDate(note.date_created)}
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            onClick={() => void deleteNote(note.id)}
                            aria-label={gettext('Delete note')}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                    {note.images.length ? (
                        <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
                            {note.images.map((image) => (
                                <Link
                                    key={image.id}
                                    href={image.image}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <Box
                                        component="img"
                                        src={image.image}
                                        alt=""
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            objectFit: 'cover',
                                            borderRadius: 0.5,
                                        }}
                                    />
                                </Link>
                            ))}
                        </Stack>
                    ) : null}
                </Box>
            ))}
        </Stack>
    )
}

export default function ReturnDetailPage() {
    const params = useParams<{ return_id: string }>()
    const returnId = Number(params.return_id)
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const [selectedAction, setSelectedAction] = useState<ReturnActionCapability | null>(null)

    const detailKey = queryKeys.returns.detail(returnId).queryKey
    const detailQuery = api.returns.detail.useQuery(
        detailKey,
        { params: { returnId } },
        {
            enabled: Number.isFinite(returnId),
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
        }
    )
    const capabilitiesQuery = api.returns.capabilities.useQuery(
        queryKeys.returns.capabilities(returnId).queryKey,
        { params: { returnId }, query: {} },
        {
            enabled: Number.isFinite(returnId),
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
        }
    )

    const returnDetail = detailQuery.data?.status === 200 ? detailQuery.data.body : undefined
    const capabilities: ReturnCapabilities | undefined =
        capabilitiesQuery.data?.status === 200 ? capabilitiesQuery.data.body : undefined

    const reload = async () => {
        await queryClient.invalidateQueries({ queryKey: ['returns'] })
    }

    const activity = useMemo(() => {
        if (!returnDetail) return []
        const failedActions = returnDetail.actions
            .filter((action) => action.status === 'failed')
            .map((action) => ({
                id: `action-${action.id}`,
                title: gettext('Action failed'),
                description: action.error_message || action.action_display_name,
                occurred_at: action.date_completed || action.date_created,
                actor: action.requested_by_name,
            }))
        const events = returnDetail.events.map((event) => ({
            id: `event-${event.id}`,
            title: event.title,
            description: event.description,
            occurred_at: event.occurred_at,
            actor: event.actor_display_name,
        }))
        return [...events, ...failedActions].sort((a, b) =>
            b.occurred_at.localeCompare(a.occurred_at)
        )
    }, [returnDetail])

    if (detailQuery.isLoading || !returnDetail) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    minHeight: 360,
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {detailQuery.isLoading ? (
                    <CircularProgress color="secondary" />
                ) : (
                    <Typography color="error">{gettext('Return could not be loaded.')}</Typography>
                )}
            </Box>
        )
    }

    return (
        <Box sx={{ width: '100%', maxWidth: 1024, mx: 'auto', p: { xs: 2, md: 3 } }}>
            <Button
                color="secondary"
                sx={{ mb: 1 }}
                onClick={() => navigate('/home/sales/returns')}
            >
                <ArrowBackIcon />
                {gettext('Returns')}
            </Button>
            <Paper sx={{ mb: 2, px: 2.5, py: 2, color: 'grey.700' }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    spacing={2}
                >
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="subtitle2">
                                {gettext('Return ID: ')}
                                <Typography
                                    component="span"
                                    variant="subtitle2"
                                    sx={{ fontWeight: 400 }}
                                >
                                    {returnDetail.return_id}
                                </Typography>
                            </Typography>
                            <Chip
                                color="secondary"
                                size="small"
                                variant="outlined"
                                label={
                                    returnDetail.status_display_name ||
                                    returnDetail.status ||
                                    gettext('Unknown')
                                }
                                sx={{ fontWeight: 'normal' }}
                            />
                            {returnDetail.is_overdue ? (
                                <Typography variant="caption" color="error">
                                    {gettext('Seller action overdue')}
                                </Typography>
                            ) : null}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {returnDetail.shop.shop_name} · {returnDetail.shop.channel.name} ·{' '}
                            {gettext('Updated')} {formatReturnDate(returnDetail.date_updated)}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                            component="img"
                            src={
                                returnDetail.shop.channel.channel_square_icon_url ||
                                returnDetail.shop.channel.channel_icon_url ||
                                ''
                            }
                            alt={returnDetail.shop.channel.name}
                            sx={{ width: 32, height: 32, objectFit: 'contain' }}
                        />
                    </Stack>
                </Stack>
                {capabilitiesQuery.isError ? (
                    <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
                        {getRequestError(
                            capabilitiesQuery.error,
                            gettext('Live channel actions could not be loaded.')
                        )}
                    </Typography>
                ) : null}
                {capabilitiesQuery.isLoading || capabilities?.actions.length ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                        {capabilitiesQuery.isLoading ? <CircularProgress size={24} /> : null}
                        {capabilities?.actions.map((action) => (
                            <Button
                                key={action.action}
                                variant={actionVariant(action.action)}
                                color={actionColor(action.action)}
                                onClick={() => setSelectedAction(action)}
                            >
                                {action.label}
                            </Button>
                        ))}
                    </Stack>
                ) : null}
            </Paper>

            <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                    <Section title={gettext('Return summary')}>
                        <Grid container spacing={2}>
                            <Grid item xs={6} sm={4}>
                                <DetailValue
                                    label={gettext('Return type')}
                                    value={returnDetail.return_type_display_name}
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <DetailValue
                                    label={gettext('Refund total')}
                                    value={formatReturnMoney(
                                        returnDetail.refund_total,
                                        returnDetail.currency
                                    )}
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <DetailValue
                                    label={gettext('Order')}
                                    value={
                                        <Link
                                            component={RouterLink}
                                            to={`/home/sale/${returnDetail.sale_pk}`}
                                            color="secondary"
                                            underline="hover"
                                        >
                                            {returnDetail.sale_id}{' '}
                                            <OpenInNewIcon sx={{ fontSize: 13 }} />
                                        </Link>
                                    }
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <DetailValue
                                    label={gettext('Created')}
                                    value={formatReturnDate(returnDetail.date_created)}
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <DetailValue
                                    label={gettext('Internal reason')}
                                    value={returnDetail.reason_display_name}
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <DetailValue
                                    label={gettext('Channel reason')}
                                    value={
                                        returnDetail.channel_reason_text ||
                                        returnDetail.channel_reason_code
                                    }
                                />
                            </Grid>
                        </Grid>
                        {returnDetail.buyer_comment ? (
                            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    {gettext('Buyer comment')}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}
                                >
                                    {returnDetail.buyer_comment}
                                </Typography>
                            </Box>
                        ) : null}
                        {returnDetail.buyer_media.length ? (
                            <Box sx={{ mt: 2 }}>
                                <MediaGrid media={returnDetail.buyer_media} />
                            </Box>
                        ) : null}
                    </Section>

                    <Section title={gettext('Items')}>
                        <Stack divider={<Divider flexItem />}>
                            {returnDetail.sale_return_line_items.map((item) => (
                                <Stack
                                    key={item.id}
                                    direction="row"
                                    spacing={1.5}
                                    sx={{ py: 1 }}
                                    alignItems="center"
                                >
                                    <Box
                                        component="img"
                                        src={item.image_url || '/static/web/img/placeholder.png'}
                                        alt=""
                                        sx={{ width: 56, height: 56, objectFit: 'contain' }}
                                    />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2">
                                            {item.product_name || gettext('Unknown item')}
                                        </Typography>
                                        <Typography sx={{ color: 'grey.500', fontSize: '0.8rem' }}>
                                            {item.variation_name || item.sku || '—'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography variant="body2" color="grey.500">
                                            × {item.quantity}
                                        </Typography>
                                        <Typography variant="body2">
                                            {formatReturnMoney(item.total, returnDetail.currency)}
                                        </Typography>
                                    </Box>
                                </Stack>
                            ))}
                        </Stack>
                    </Section>

                    {returnDetail.seller_media.length || returnDetail.seller_evidence_comment ? (
                        <Section title={gettext('Seller evidence on channel')}>
                            {returnDetail.seller_evidence_comment ? (
                                <Typography
                                    variant="body2"
                                    sx={{ mb: returnDetail.seller_media.length ? 1.5 : 0 }}
                                >
                                    {returnDetail.seller_evidence_comment}
                                </Typography>
                            ) : null}
                            <MediaGrid media={returnDetail.seller_media} />
                        </Section>
                    ) : null}

                    <Section title={gettext('Logistics')}>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 2 }}>
                            {returnDetail.logistics.status || gettext('No tracking updates yet')}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <DetailValue
                                    label={pgettext('Return logistics', 'Tracking number')}
                                    value={returnDetail.logistics.tracking_code}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <DetailValue
                                    label={gettext('Provider')}
                                    value={returnDetail.logistics.provider}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <DetailValue
                                    label={gettext('Shipping type')}
                                    value={returnDetail.logistics.shipping_type}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <DetailValue
                                    label={gettext('Handover method')}
                                    value={returnDetail.logistics.handover_method}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <DetailValue
                                    label={gettext('Return address')}
                                    value={
                                        typeof returnDetail.logistics.return_address === 'string'
                                            ? returnDetail.logistics.return_address
                                            : returnDetail.logistics.return_address
                                              ? JSON.stringify(
                                                    returnDetail.logistics.return_address
                                                )
                                              : undefined
                                    }
                                />
                            </Grid>
                        </Grid>
                    </Section>

                    <Section title={gettext('Activity')}>
                        {activity.length ? (
                            <Stack spacing={0}>
                                {activity.map((event, index) => (
                                    <Box
                                        key={event.id}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '9px minmax(0, 1fr)',
                                            columnGap: 1.5,
                                            pb: index === activity.length - 1 ? 0 : 2,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: 'relative',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 6,
                                                    left: '50%',
                                                    width: 8,
                                                    height: 8,
                                                    transform: 'translateX(-50%)',
                                                    borderRadius: '50%',
                                                    bgcolor: 'grey.400',
                                                }}
                                            />
                                            {index !== activity.length - 1 ? (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 18,
                                                        bottom: -18,
                                                        left: '50%',
                                                        width: '1px',
                                                        transform: 'translateX(-50%)',
                                                        bgcolor: 'grey.300',
                                                    }}
                                                />
                                            ) : null}
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" fontWeight={500}>
                                                {event.title}
                                            </Typography>
                                            {event.description ? (
                                                <Typography variant="body2" color="text.secondary">
                                                    {event.description}
                                                </Typography>
                                            ) : null}
                                            <Typography variant="caption" color="text.secondary">
                                                {formatReturnDate(event.occurred_at)}
                                                {event.actor ? ` · ${event.actor}` : ''}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {gettext('No activity recorded yet.')}
                            </Typography>
                        )}
                    </Section>

                    {returnDetail.actions.length ? (
                        <Section title={gettext('Seller submissions')}>
                            <Stack spacing={2} divider={<Divider flexItem />}>
                                {returnDetail.actions.map((action) => (
                                    <Box key={action.id}>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            spacing={1}
                                        >
                                            <Typography variant="body2" fontWeight={500}>
                                                {action.action_display_name}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color={
                                                    action.status === 'failed'
                                                        ? 'error.main'
                                                        : action.status === 'succeeded'
                                                          ? 'success.main'
                                                          : 'text.secondary'
                                                }
                                            >
                                                {action.status_display_name}
                                            </Typography>
                                        </Stack>
                                        {action.channel_reason_text || action.decision_reason ? (
                                            <Typography variant="body2" color="text.secondary">
                                                {action.channel_reason_text ||
                                                    action.decision_reason_display_name}
                                            </Typography>
                                        ) : null}
                                        {action.comment ? (
                                            <Typography
                                                variant="body2"
                                                sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
                                            >
                                                {action.comment}
                                            </Typography>
                                        ) : null}
                                        {action.images.length ? (
                                            <MediaGrid
                                                media={action.images.map((image) => ({
                                                    url: image.image,
                                                    type: 'image',
                                                    thumbnail_url: image.image,
                                                }))}
                                            />
                                        ) : null}
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            sx={{ mt: 0.75 }}
                                        >
                                            {action.requested_by_name || gettext('User')} ·{' '}
                                            {formatReturnDate(action.date_created)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Section>
                    ) : null}
                </Grid>

                <Grid item xs={12} md={4}>
                    <Section title={gettext('Deadlines')}>
                        <Stack spacing={2}>
                            <DetailValue
                                label={gettext('Seller action due')}
                                value={formatReturnDate(returnDetail.seller_action_due_at)}
                            />
                            <DetailValue
                                label={gettext('Buyer ship due')}
                                value={formatReturnDate(returnDetail.buyer_ship_due_at)}
                            />
                        </Stack>
                    </Section>
                    <Section title={gettext('Notes')}>
                        <Notes returnDetail={returnDetail} onChanged={reload} />
                    </Section>
                </Grid>
            </Grid>

            <ActionDialog
                key={selectedAction?.action || 'closed'}
                capability={selectedAction}
                returnId={returnId}
                onClose={() => setSelectedAction(null)}
                onComplete={reload}
            />
        </Box>
    )
}
